import { useId } from 'react';

/**
 * El hexágono es el motivo gráfico de la marca — según el brandbook (p. 4)
 * remite a la nube de datos. Aquí vive en tres formas: viñeta rellena, marcador
 * numerado y patrón de fondo.
 */

/** Polígono base del hexágono, normalizado a un viewBox de 0 0 104 108. */
const HEX_POINTS = '52,2 100,29 100,81 52,106 4,81 4,29';

interface HexBulletProps {
  readonly className?: string;
}

/** Viñeta rellena para los checklists de la franja de autoridad. */
export function HexBullet({ className }: HexBulletProps) {
  return (
    <svg
      viewBox="0 0 104 108"
      className={className}
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <polygon points={HEX_POINTS} fill="currentColor" />
    </svg>
  );
}

/** Hexágono en contorno — usado en la línea de confianza del hero. */
export function HexOutline({ className }: HexBulletProps) {
  return (
    <svg
      viewBox="0 0 104 108"
      className={className}
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <polygon
        points={HEX_POINTS}
        fill="none"
        stroke="currentColor"
        strokeWidth="7"
      />
    </svg>
  );
}

interface HexMarkerProps {
  readonly number: string;
  readonly className?: string;
}

/**
 * Marcador numerado de las cards de funcionalidades: hexágono en contorno con
 * el número centrado. El número va como texto real dentro del SVG para que
 * quede en el árbol de accesibilidad.
 */
export function HexMarker({ number, className }: HexMarkerProps) {
  return (
    <svg viewBox="0 0 104 108" className={className} focusable="false">
      <polygon
        points={HEX_POINTS}
        fill="none"
        stroke="currentColor"
        strokeWidth="5"
      />
      <text
        x="52"
        y="54"
        textAnchor="middle"
        dominantBaseline="central"
        fill="currentColor"
        fontSize="38"
        fontWeight="600"
        fontFamily="Poppins, sans-serif"
      >
        {number}
      </text>
    </svg>
  );
}

interface HexPatternProps {
  readonly className?: string;
  /** Opacidad del trazo, 0–1. El handoff especifica 0.05–0.10 según sección. */
  readonly opacity?: number;
}

/**
 * Patrón de hexágonos en mosaico, usado como fondo decorativo.
 *
 * Se renderiza como SVG inline y no como imagen de fondo en CSS: una
 * `background-image: url("data:image/svg+xml,...")` exigiría relajar `img-src`
 * o inyectar estilos inline, y ambas cosas chocan con la CSP estricta.
 */
export function HexPattern({ className, opacity = 0.06 }: HexPatternProps) {
  const patternId = useId();

  return (
    <svg
      className={className}
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <pattern
          id={patternId}
          width="104"
          height="120"
          patternUnits="userSpaceOnUse"
        >
          <polygon
            points="52,8 92,31 92,77 52,100 12,77 12,31"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            opacity={opacity}
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${patternId})`} />
    </svg>
  );
}
